import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useForm } from "@tanstack/react-form";
import z from "zod";
import {
  Laptop,
  Trash2,
  Edit2,
  Calendar,
  Globe,
  Loader2,
  X,
  FileText,
  Plus,
  Key,
} from "lucide-react";

import { orpc } from "@/utils/orpc";
import { authClient } from "@/lib/auth-client";

export const Route = createFileRoute("/_auth/admin/terminals")({
  component: TerminalsManagementPage,
});

interface TerminalItem {
  id: string;
  name: string | null;
  organizationId: string | null;
  userAgent: string | null;
  ipAddress: string | null;
  createdAt: Date;
}

function getFriendlyUserAgent(ua?: string | null) {
  if (!ua) return "Bilinmeyen Cihaz";
  const uaLower = ua.toLowerCase();

  let os = "Bilinmeyen İşletim Sistemi";
  if (uaLower.includes("windows")) os = "Windows";
  else if (uaLower.includes("mac os") || uaLower.includes("macintosh")) os = "macOS";
  else if (uaLower.includes("linux")) os = "Linux";
  else if (uaLower.includes("android")) os = "Android";
  else if (uaLower.includes("iphone") || uaLower.includes("ipad")) os = "iOS";

  let clientType = "Tarayıcı";
  if (uaLower.includes("electrobun")) clientType = "Masaüstü Uygulaması";
  else if (uaLower.includes("firefox")) clientType = "Firefox";
  else if (uaLower.includes("chrome")) clientType = "Chrome";
  else if (uaLower.includes("safari") && !uaLower.includes("chrome")) clientType = "Safari";
  else if (uaLower.includes("edge")) clientType = "Edge";

  return `${os} • ${clientType}`;
}

function TerminalsManagementPage() {
  const { data: activeOrg } = authClient.useActiveOrganization();
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState<TerminalItem | null>(null);

  // Query terminals using ORPC queryOptions
  const {
    data: terminals = [],
    refetch,
    isLoading,
  } = useQuery({
    ...orpc.restaurant.getTerminals.queryOptions(),
    enabled: !!activeOrg?.id,
  });

  // Delete terminal mutation
  const deleteMutation = useMutation(
    orpc.restaurant.deleteTerminal.mutationOptions({
      onSuccess: () => {
        toast.success(
          "Cihaz yetkilendirmesi başarıyla kaldırıldı. Cihaz otomatik olarak çıkış yapacak.",
        );
        refetch();
      },
      onError: (err: any) => {
        toast.error(err.message || "Cihaz kaldırılırken bir hata oluştu.");
      },
    }),
  );

  // Rename terminal mutation
  const renameMutation = useMutation(
    orpc.restaurant.renameTerminal.mutationOptions({
      onSuccess: () => {
        toast.success("Cihaz ismi başarıyla güncellendi.");
        setIsRenameModalOpen(false);
        renameForm.reset();
        setSelectedTerminal(null);
        refetch();
      },
      onError: (err: any) => {
        toast.error(err.message || "Cihaz ismi güncellenirken bir hata oluştu.");
      },
    }),
  );

  // Approve new terminal mutation
  const approveMutation = useMutation(
    orpc.restaurant.approveTerminal.mutationOptions({
      onSuccess: () => {
        toast.success("Cihaz başarıyla eklendi ve yetkilendirildi.");
        setIsAddModalOpen(false);
        addForm.reset();
        refetch();
      },
      onError: (err: any) => {
        toast.error(err.message || "Cihaz eklenirken bir hata oluştu.");
      },
    }),
  );

  // TanStack Form for adding a terminal
  const addForm = useForm({
    defaultValues: {
      code: "",
      name: "",
    },
    onSubmit: async ({ value }) => {
      approveMutation.mutate({
        userCode: value.code.trim(),
        name: value.name.trim(),
      });
    },
    validators: {
      onSubmit: z.object({
        code: z.string().min(6, "Aktivasyon kodu en az 6 karakter olmalıdır"),
        name: z.string().min(1, "Cihaz adı boş olamaz"),
      }),
    },
  });

  // TanStack Form for renaming a terminal
  const renameForm = useForm({
    defaultValues: {
      name: "",
    },
    onSubmit: async ({ value }) => {
      if (!selectedTerminal) return;
      renameMutation.mutate({
        id: selectedTerminal.id,
        name: value.name.trim(),
      });
    },
    validators: {
      onSubmit: z.object({
        name: z.string().min(1, "Cihaz adı boş olamaz"),
      }),
    },
  });

  const handleRemove = (terminal: TerminalItem) => {
    if (
      confirm(
        `"${terminal.name}" isimli cihazın yetkisini kaldırmak istediğinize emin misiniz? Bu cihazdaki uygulama otomatik olarak sıfırlanacaktır.`,
      )
    ) {
      deleteMutation.mutate({ id: terminal.id });
    }
  };

  const openRenameModal = (terminal: TerminalItem) => {
    setSelectedTerminal(terminal);
    renameForm.setFieldValue("name", terminal.name || "");
    setIsRenameModalOpen(true);
  };

  return (
    <div className="space-y-6 pb-6 animate-in fade-in duration-300">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900">Cihaz Yönetimi</h1>
          <p className="text-sm text-gray-500">
            Kafenizde aktif olarak çalışan tüm POS terminallerini ve cihazları buradan yönetin.
          </p>
        </div>
        <button
          onClick={() => {
            addForm.reset();
            setIsAddModalOpen(true);
          }}
          className="flex items-center gap-1.5 rounded-xl bg-gradient-to-b from-[#2d2e32] to-[#1e1f22] text-white px-4 py-2.5 text-xs font-bold shadow-md hover:from-[#383a3f] hover:to-[#24252a] active:scale-[0.99] transition-all cursor-pointer select-none"
        >
          <Plus className="size-4" />
          <span>Yeni Cihaz Yetkilendir</span>
        </button>
      </div>

      {/* Main List */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Loader2 className="size-6 animate-spin text-gray-400" />
            <span className="ml-2.5 text-xs text-gray-500 font-bold">
              Cihaz listesi yükleniyor...
            </span>
          </div>
        ) : terminals.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-gray-50 border border-gray-100 text-gray-400 mb-4 shadow-inner">
              <Laptop className="size-6" />
            </div>
            <h3 className="text-sm font-bold text-gray-800">Aktif Cihaz Yok</h3>
            <p className="mt-1 text-xs text-gray-400 max-w-[280px] leading-relaxed">
              Bu restorana bağlı aktif masaüstü cihazı veya terminal bulunmuyor. Masaüstü
              uygulamasını açıp ekranda görünen kodu eklemek için sağ üstteki **Yeni Cihaz
              Yetkilendir** butonuna tıklayın.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-bold uppercase tracking-wider text-gray-400 select-none">
                  <th className="px-6 py-4">Cihaz Adı / Durum</th>
                  <th className="px-6 py-4">Cihaz Tipi</th>
                  <th className="px-6 py-4">Benzersiz ID (UUID)</th>
                  <th className="px-6 py-4">IP Adresi</th>
                  <th className="px-6 py-4">Aktivasyon Tarihi</th>
                  <th className="px-6 py-4 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-xs">
                {terminals.map((terminal) => (
                  <tr key={terminal.id} className="hover:bg-gray-50/40 transition-colors group">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex size-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600 border border-blue-100 shadow-sm">
                          <Laptop className="size-4" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-800">{terminal.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            <span className="text-[10px] text-gray-400 font-semibold uppercase">
                              Aktif
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-medium">
                      {getFriendlyUserAgent(terminal.userAgent)}
                    </td>
                    <td
                      className="px-6 py-4  text-[10px] text-gray-400 select-all"
                      title={terminal.id}
                    >
                      {terminal.id}
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-medium">
                      <span className="flex items-center gap-1">
                        <Globe className="size-3 text-gray-300" />
                        {terminal.ipAddress || "Lokal Ağ"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 font-medium">
                      <span className="flex items-center gap-1">
                        <Calendar className="size-3" />
                        {new Date(terminal.createdAt).toLocaleDateString("tr-TR", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5 opacity-80 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openRenameModal(terminal)}
                          title="İsmi Düzenle"
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-all cursor-pointer"
                        >
                          <Edit2 className="size-3.5" />
                        </button>
                        <button
                          onClick={() => handleRemove(terminal)}
                          disabled={deleteMutation.isPending}
                          title="Cihaz Yetkisini Kaldır"
                          className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-all cursor-pointer disabled:opacity-50"
                        >
                          {deleteMutation.isPending && selectedTerminal?.id === terminal.id ? (
                            <Loader2 className="size-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="size-3.5" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Terminal Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-[400px] rounded-2xl border border-white/60 bg-white p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4 select-none">
              <div className="flex items-center gap-2">
                <Plus className="size-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">Yeni Cihaz Yetkilendir</h3>
              </div>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addForm.handleSubmit();
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                  Aktivasyon Kodu (Cihaz Ekranındaki Kod)
                </label>
                <addForm.Field name="code">
                  {(field) => (
                    <div>
                      <div className="relative flex items-center">
                        <Key className="absolute left-3 size-4 text-gray-400 pointer-events-none" />
                        <input
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          type="text"
                          required
                          maxLength={7}
                          placeholder="Örn: ABC-D12"
                          className="w-full h-10 pl-9 pr-3 bg-[#f0f2f6] border-none rounded-xl text-xs text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:ring-2 focus:ring-black/10 transition-all duration-200  uppercase"
                        />
                      </div>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-[10px] text-red-500 mt-1 font-semibold">
                          {field.state.meta.errors
                            .map((error: any) => error?.message || error)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </addForm.Field>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                  Cihaz Adı
                </label>
                <addForm.Field name="name">
                  {(field) => (
                    <div>
                      <div className="relative flex items-center">
                        <FileText className="absolute left-3 size-4 text-gray-400 pointer-events-none" />
                        <input
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          type="text"
                          required
                          placeholder="Örn: Giriş Kasa, Mutfak POS"
                          className="w-full h-10 pl-9 pr-3 bg-[#f0f2f6] border-none rounded-xl text-xs text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:ring-2 focus:ring-black/10 transition-all duration-200"
                        />
                      </div>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-[10px] text-red-500 mt-1 font-semibold">
                          {field.state.meta.errors
                            .map((error: any) => error?.message || error)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </addForm.Field>
              </div>

              <div className="flex gap-2.5 pt-2 select-none">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="flex-1 h-10 border border-gray-200 text-gray-500 rounded-xl text-xs font-semibold hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={approveMutation.isPending}
                  className="flex-1 h-10 bg-gradient-to-b from-[#2d2e32] to-[#1e1f22] text-white rounded-xl text-xs font-semibold shadow-[0_4px_12px_rgba(0,0,0,0.12)] hover:from-[#383a3f] hover:to-[#24252a] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {approveMutation.isPending ? (
                    <>
                      <Loader2 className="size-3 animate-spin text-white" />
                      <span>Yetkilendiriliyor...</span>
                    </>
                  ) : (
                    <span>Cihazı Yetkilendir</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {isRenameModalOpen && selectedTerminal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-[400px] rounded-2xl border border-white/60 bg-white p-6 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-4 select-none">
              <div className="flex items-center gap-2">
                <Laptop className="size-4 text-blue-600" />
                <h3 className="text-sm font-bold text-gray-800">Cihaz İsmini Güncelle</h3>
              </div>
              <button
                onClick={() => setIsRenameModalOpen(false)}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                renameForm.handleSubmit();
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1.5">
                  Cihaz Adı
                </label>
                <renameForm.Field name="name">
                  {(field) => (
                    <div>
                      <div className="relative flex items-center">
                        <FileText className="absolute left-3 size-4 text-gray-400 pointer-events-none" />
                        <input
                          id={field.name}
                          name={field.name}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          type="text"
                          required
                          placeholder="Örn: Giriş Kasa, Mutfak POS"
                          className="w-full h-10 pl-9 pr-3 bg-[#f0f2f6] border-none rounded-xl text-xs text-gray-900 placeholder:text-gray-400 outline-none focus:bg-white focus:ring-2 focus:ring-black/10 transition-all duration-200"
                        />
                      </div>
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-[10px] text-red-500 mt-1 font-semibold">
                          {field.state.meta.errors
                            .map((error: any) => error?.message || error)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                  )}
                </renameForm.Field>
              </div>

              <div className="flex gap-2.5 pt-2 select-none">
                <button
                  type="button"
                  onClick={() => setIsRenameModalOpen(false)}
                  className="flex-1 h-10 border border-gray-200 text-gray-500 rounded-xl text-xs font-semibold hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={renameMutation.isPending}
                  className="flex-1 h-10 bg-gradient-to-b from-[#2d2e32] to-[#1e1f22] text-white rounded-xl text-xs font-semibold shadow-[0_4px_12px_rgba(0,0,0,0.12)] hover:from-[#383a3f] hover:to-[#24252a] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  {renameMutation.isPending ? (
                    <>
                      <Loader2 className="size-3 animate-spin text-white" />
                      <span>Kaydediliyor...</span>
                    </>
                  ) : (
                    <span>Değişiklikleri Kaydet</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
